import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { utils } from '@yola/ws-sdk';
import mainMenu from 'src/js/modules/main-menu';
import SubMenuGroup from './sub-menu-group';
import NavMenuItem from './nav-menu-item';
import Banner from '../banner';
import BrandContainer from '../../../brand/containers/brand-container';
import ChecklistProgressLine from '../../../site-progress-checklist/components/checklist-progress-line';
import getVisibilityStatus from '../../selectors/get-visibility-status';

const defaultChecklistProgressData = {};
const defaultBanners = [];

function NavCollapse({
  items,
  onShow = utils.noop,
  isBrand = true,
  banners = defaultBanners,
  checklistProgressData = defaultChecklistProgressData,
}) {
  const isOpen = useSelector(getVisibilityStatus);
  const close = useCallback(() => mainMenu.operations.hide(), []);

  const { isEnabled, steps, stepsCompleted } = checklistProgressData;
  const isChecklistProgress = isEnabled && stepsCompleted < steps;

  const classes = classNames('ws-nav__collapse', {
    'ws-nav__collapse--active': isOpen,
  });
  const headerClasses = classNames('ws-nav__collapse-header', {
    'ws-nav__collapse-header--separator': isChecklistProgress,
  });

  useEffect(() => {
    if (!isOpen) {
      SubMenuGroup.dispatchClose();
    } else {
      onShow();

      banners.forEach(({ onShow: onBannerShow }) => {
        if (onBannerShow) {
          onBannerShow();
        }
      });
    }
  }, [isOpen, onShow, banners]);

  return (
    <div className={classes}>
      <div className="ws-nav__collapse-inner">
        {(isBrand || isChecklistProgress) && (
          <div className={headerClasses}>
            {isBrand && <BrandContainer />}
            {isChecklistProgress && (
              <ChecklistProgressLine
                captions={{
                  heading: checklistProgressData.heading,
                  title: checklistProgressData.title,
                }}
                steps={{
                  all: steps,
                  completed: stepsCompleted,
                }}
                onClick={() => {
                  close();
                  checklistProgressData.onClick();
                }}
              />
            )}
          </div>
        )}
        <ul className="ws-nav__collapse-main">
          {items
            .filter((item) => item.id !== 'dashboard')
            .map((item, index) => (
              // eslint-disable-next-line yola/react/no-array-index-key
              <NavMenuItem key={index} item={item} />
            ))}
        </ul>
        <div className="ws-nav__collapse-footer">
          {banners.map((banner, indx) => {
            const { id, isInteractive, icon, captions, onClick, appearance } = banner;
            return (
              <Banner
                // eslint-disable-next-line yola/react/no-array-index-key
                key={`${id}_${indx}`}
                interactive={isInteractive}
                icon={icon}
                captions={captions}
                appearance={appearance}
                onClick={() => {
                  if (onClick) {
                    close();
                    onClick();
                  }
                }}
              />
            );
          })}
          {items
            .filter((item) => item.id === 'dashboard')
            .map((item, index) => (
              // eslint-disable-next-line yola/react/no-array-index-key
              <ul key={index} className="ws-nav__collapse-footer-list">
                <NavMenuItem item={item} />
              </ul>
            ))}
        </div>
      </div>
    </div>
  );
}

NavCollapse.propTypes = {
  items: PropTypes.array.isRequired,
  onShow: PropTypes.func,
  isBrand: PropTypes.bool,
  banners: PropTypes.array,
  checklistProgressData: PropTypes.shape({
    isEnabled: PropTypes.bool,
    heading: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    steps: PropTypes.number.isRequired,
    stepsCompleted: PropTypes.number.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
};

export default NavCollapse;
